import React, { useState, useEffect }                from 'react';
import { Header, Modal, Segment, Icon, Container, Message, Button, Grid, }  from 'semantic-ui-react';
import { Link }                                     from 'react-router-dom';

import Privileges                 from 'constants/Privileges';
import { contentNamespace }       from 'utils/content';
import formattedDate              from 'utils/formattedDate';
import formattedAge               from 'utils/formattedAge';
import WithPermission             from 'containers/WithPermission';
import DataList                   from 'components/DataList';
import ClientResource             from 'resources/organization/ClientResource';
import MergeClientsResource       from 'resources/organization/MergeClientsResourceResource';
import useOrganizationResource    from 'hooks/useOrganizationResource';
import useOrganizationFetcher     from 'hooks/useOrganizationFetcher';

import ClientLinkModal from 'caseWorthy/components/ClientLinkModal';

import Fields     from '../Fields';
import Forms      from '../Forms';
import MergeLink  from './MergeLink';
import ProfilePhoto from './ProfilePhoto';
import Household from './Household';

// -----------------------------------------------------
// Helpers
// -----------------------------------------------------

const genderLabels = contentNamespace('organization.clients.gender');
function getClientDetails({ id, dob, dod, gender, active }) {

  const details = [
    { label: 'Client ID', value: id },
    { label: 'DOB',       value: formattedAge(dob, dod) },
    { label: 'Gender',    value: genderLabels(gender) }
  ];

  if (dod !== null) {
    details.push( { label: 'Date Deceased', value: formattedDate(dod, 'date', '') });
  }

  if (!active) {
    details.push( { label: 'Status', value: 'Inactive' } );
  }

  return details;
}

// -----------------------------------------------------
// Component Definition
// -----------------------------------------------------

const DetailView = (props) => {
  const { match: { params: { id } } } = props;

  const initialClient                           = useOrganizationResource(ClientResource.detailShape(), { id });
  const merge                                   = useOrganizationFetcher(MergeClientsResource.createShape());
  const [opened, setOpened]                     = useState(false);
  const [client, setClient]                     = useState(initialClient);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDistinctModal, setShowDistinctModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const { first_name,
          middle_name,
          last_name,
          photo_url,
          has_duplicates,
          referred_not_linked,
          duplicates=[] } = client;

  const fullName            = [first_name || '...', middle_name, last_name].join(' ');
  const dataListItems       = getClientDetails(client);

  useEffect(() => {
    setClient(initialClient);
  }, [initialClient, setClient]);

  useEffect(() => {
    setOpened(referred_not_linked);
  }, [referred_not_linked, setOpened]);

  const handleMergeSave = async ({ masterId, mergedIds, is_duplicate=true }, done) => {
    const { match: { params: { id } } } = props;

    const data = {
      master_client_id: masterId,
      client_ids:       mergedIds,
      is_duplicate:     is_duplicate
    };
    
    const response = await merge({}, data);
    
    if(response && !response.errors) {
      if (id === masterId) {
        done();
      }

      setShowMergeModal(false);

      if (data.is_duplicate) {
        setShowSuccessModal(true);
      } else {
        setShowDistinctModal(true);
      }
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const closeDistinctModal = () => {
    setShowDistinctModal(false);
  };

  const handleClick = (evt) => {
    evt.preventDefault();
    setOpened(true);
  };

  const handleClose = (result) => {
    if(result === null || result === undefined) {
      setOpened(false);
    } else {
      if(!result.errors) {
        setClient(result);
      }
    }
  };

  return (
    <Container>
      <Modal
        open={showSuccessModal}
        dimmer='inverted'
        size='tiny'
        onClose={ closeSuccessModal } >
          <Modal.Header>Success!</Modal.Header>
          <Modal.Content>
            The merge has been requested. <br />
            You will receive an email when it has been completed.
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={ closeSuccessModal } positive basic>Ok</Button>
          </Modal.Actions>
      </Modal>

      <Modal
        open={showDistinctModal}
        dimmer='inverted'
        size='tiny'
        onClose={ closeDistinctModal } >
          <Modal.Header>Success!</Modal.Header>
          <Modal.Content>
            The clients have been marked as distinct.
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={ closeDistinctModal } positive basic>Ok</Button>
          </Modal.Actions>
      </Modal>

      { id
        && <ClientLinkModal client={ client }
                            opened={ opened }
                            dataList={ [{ label: 'Name', value: fullName }, ...dataListItems] }
                            onClose={ handleClose } />
      }

      <Segment loading={ !client }>
        <Grid>
          <Grid.Column width={4}>
            { id && <ProfilePhoto id={ id } photoUrl={ photo_url } /> }
          </Grid.Column>

          <Grid.Column width={12}>
            <Header as='h2'>
              <Link to={ `/organization/clients/${id}`}>
                { fullName }
              </Link>
            </Header>
            {
              id
              && <DataList items={ dataListItems } />
            }
          </Grid.Column>
        </Grid>

        {
          has_duplicates &&
          <Message warning icon>
            <Icon name='exclamation' color='yellow' />
            <Message.Content>
              <Message.Header>
                This client appears to have a duplicate record.  Would you like
                to&nbsp;
                <MergeLink  opened={showMergeModal}
                            client={ client }
                            duplicates={ duplicates }
                            setShowMergeModal={setShowMergeModal}
                            onSubmit={ handleMergeSave }>
                  merge these records
                </MergeLink>?
              </Message.Header>
            </Message.Content>
          </Message>
        }

        {
          referred_not_linked &&
          <Message error icon>
            <Icon name='exclamation' color='red' />
            <Message.Content>
              <p>
                This client has pending DTD referrals, but the referrals
                cannot be sent until the client has been linked to the DTD system.
              </p>
              <Button onClick={ handleClick }
                      size='tiny'
                      color='red'>
                Link Client Record
              </Button>
            </Message.Content>
          </Message>
        }
      </Segment>

      { id && props.match.isExact &&
        <>
          <Fields client={ client } />
          <Household client={ client } />
        </>
      }

      <WithPermission resource='client_forms'
                      actions={ [Privileges.read, Privileges.write] }
                      anyRecord={ true }>

        <Forms { ...props } />
      </WithPermission>
    </Container>
  );
};

// -----------------------------------------------------
// Exports
// -----------------------------------------------------

export default DetailView;
